import { cleanParse } from "@intelligems/ig-utils";
import { Middleware } from "@reduxjs/toolkit";

import type { RootState } from "@src/store/rootState";
import {
  clearSitewideDashboardFilters,
  setSitewideDashboardFilter,
  updateSitewideDashboardFilter,
  updateSitewideDashboardInterval,
} from "../slices/sitewideDashboardFilters";
import {
  ISitewideDashboardFilters,
  ISitewideDashboardFiltersSchema,
} from "../types/sitewideDashboardFilters";

import {
  deserializeValue,
  serializeFilterValue,
} from "./dashboardFiltersUrlHelpers";

// URL parameter mapping - clean names without prefixes
const URL_PARAM_MAP: Record<keyof ISitewideDashboardFilters, string> = {
  audienceRatioToggle: "ratio",
  channels: "channels",
  conversionFunnel: "", // Not synced
  countryCodes: "countries",
  countryCodesFilterType: "countriesFilterType",
  customerType: "customer",
  deviceType: "device",
  endTime: "endTime",
  interval: "interval",
  isTimeRangeUpdated: "", // Not synced
  isZScoreEnabled: "zEnabled",
  landingPage: "landing",
  landingPageFilters: "landingFilters",
  landingPageTypes: "landingTypes",
  maxZScore: "maxZ",
  ordersMatchedToSession: "orders",
  pageName: "", // Not synced (in URL path)
  snapshotsComparisonEndTime: "snapshotsCompEnd",
  snapshotsComparisonStartTime: "snapshotsCompStart",
  snapshotsComparisonTimeFrame: "snapshotsCompTimeFrame",
  snapshotsEndTime: "snapshotsEnd",
  snapshotsStartTime: "snapshotsStart",
  snapshotsTimeFrame: "snapshotsTimeFrame",
  startTime: "startTime",
  visitorType: "visitor",
};

// Fields to exclude from URL sync
const toExclude: Set<keyof ISitewideDashboardFilters> = new Set([
  "isTimeRangeUpdated", // Internal state only
  "pageName", // This is in the URL path, not query params
]);

export const SYNCABLE_SITEWIDE_FILTERS: (keyof ISitewideDashboardFilters)[] = (
  Object.keys(URL_PARAM_MAP) as (keyof ISitewideDashboardFilters)[]
).filter((i) => !toExclude.has(i));

export const updateUrlFromSitewideFilters = (
  filters: Partial<ISitewideDashboardFilters>
) => {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Clear all existing filter params
  SYNCABLE_SITEWIDE_FILTERS.forEach((key) => {
    const paramName = URL_PARAM_MAP[key];
    if (paramName) {
      params.delete(paramName);
    }
  });

  // Set new params
  SYNCABLE_SITEWIDE_FILTERS.forEach((key) => {
    const value = filters[key];
    const serialized = serializeFilterValue(value);

    // Keeps the URL clear of end and start times when timeframe (equivalent to a dynamic set of dates) is active
    if (key === "snapshotsEndTime" && filters.snapshotsTimeFrame) return;
    if (key === "snapshotsStartTime" && filters.snapshotsTimeFrame) return;
    if (
      key === "snapshotsComparisonEndTime" &&
      filters.snapshotsComparisonTimeFrame
    )
      return;
    if (
      key === "snapshotsComparisonStartTime" &&
      filters.snapshotsComparisonTimeFrame
    )
      return;

    const paramName = URL_PARAM_MAP[key];
    if (serialized !== null && paramName) {
      params.set(paramName, serialized);
    }
  });

  window.history.replaceState(null, "", url.toString());
};

export const isSitewideAnalyticsPage = (): boolean => {
  const path = window.location.pathname;
  return /^\/sitewide-analytics/.test(path);
};

export const getSitewideFiltersFromUrl =
  (): Partial<ISitewideDashboardFilters> => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const filters: Partial<ISitewideDashboardFilters> = {};

    // Reverse map URL params to filter keys
    const reverseMap: Record<string, keyof ISitewideDashboardFilters> = {};
    (Object.keys(URL_PARAM_MAP) as (keyof ISitewideDashboardFilters)[]).forEach(
      (key) => {
        const paramName = URL_PARAM_MAP[key];
        if (paramName) {
          reverseMap[paramName] = key;
        }
      }
    );

    // Read from URL params
    Object.entries(reverseMap).forEach(([paramName, filterKey]) => {
      if (params.has(paramName)) {
        const value = params.get(paramName);
        if (value !== null) {
          filters[filterKey] = deserializeValue(value, filterKey as any);
        }
      }
    });

    return cleanParse(
      ISitewideDashboardFiltersSchema,
      filters
    ) as Partial<ISitewideDashboardFilters>;
  };

// Double assertion bypasses expensive RootState middleware type check (saves ~690ms in tsc)
export const sitewideDashboardFiltersUrlSyncMiddleware = ((store) =>
  (next) =>
  (action) => {
    const result = next(action);

    if (!isSitewideAnalyticsPage()) {
      return result;
    }

    const filterActions: string[] = [
      updateSitewideDashboardFilter.type,
      updateSitewideDashboardInterval.type,
      setSitewideDashboardFilter.type,
      clearSitewideDashboardFilters.type,
      // Note: setSitewidePageName is NOT included because page is in URL path
    ];

    if (typeof action === "object" && action !== null && "type" in action) {
      const actionType = (action as { type: string }).type;
      if (filterActions.includes(actionType)) {
        const state = store.getState();
        const filters = state.sitewideDashboardFilters;

        updateUrlFromSitewideFilters(filters);
      }
    }

    return result;
  }) as Middleware<object, RootState>;
